<script lang="ts">
  import { untrack } from 'svelte';
  import { SHIP_NAMES, shipName } from '../../game/constants/ships';
  import { upgradeForPath, upgradeLabel } from '../../game/constants/upgrades';
  import { LOADOUT_SCREEN } from '../../game/constants/hud';
  import {
    STAT_COLORS,
    STAT_DEFAULT,
    STAT_KEYS,
    STAT_LABELS,
    STAT_MAX,
    type StatKey,
  } from '../../game/constants/customize';
  import {
    canConfirm as canConfirmBuild,
    canDecrease as canDecreaseStat,
    canIncrease as canIncreaseStat,
    costOf,
    pillState as pillStateOf,
    remaining as remainingOf,
  } from '../../game/customize-build';
  import type {
    LoadoutBuild,
    LoadoutConfirmResult,
    LoadoutStats,
    LoadoutView,
  } from '../../account/loadout-client';
  import { AccountError } from '../../account/account-client';
  import ShipArt from '../ShipArt.svelte';
  import MetalFrame from '../hud/MetalFrame.svelte';

  interface Props {
    initialShipId: number;
    team: number;
    loggedIn: boolean;
    credits: number;
    loadLoadout: (shipId: number) => Promise<LoadoutView>;
    confirmBuild: (
      shipId: number,
      build: LoadoutBuild,
      expectedPrice: number | null,
    ) => Promise<LoadoutConfirmResult>;
    onclose: () => void;
  }

  const { initialShipId, team, loggedIn, credits, loadLoadout, confirmBuild, onclose }: Props =
    $props();

  const STOCK_STATS: LoadoutStats = STAT_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: STAT_DEFAULT }),
    {} as LoadoutStats,
  );

  let shipId = $state(untrack(() => initialShipId));

  let loading = $state(true);
  let loadError = $state('');
  let saved = $state<LoadoutView | null>(null);

  let pending = $state<LoadoutStats>({ ...STOCK_STATS });
  let pendingSlot1 = $state<number | null>(null);
  let pendingSlot2 = $state<number | null>(null);

  let confirming = $state(false);
  let confirmError = $state('');

  let pickerSlot = $state<1 | 2 | null>(null);

  function resetPendingFromSaved(build: LoadoutView | null): void {
    pending = { ...(build?.stats ?? STOCK_STATS) };
    pendingSlot1 = build?.slot1AbilityId ?? null;
    pendingSlot2 = build?.slot2AbilityId ?? null;
  }

  function messageOf(error: unknown): string {
    return error instanceof AccountError ? error.message : 'Something went wrong. Try again.';
  }

  async function load(id: number): Promise<void> {
    loading = true;
    loadError = '';
    confirmError = '';
    if (!loggedIn) {
      saved = null;
      resetPendingFromSaved(null);
      loading = false;
      return;
    }
    try {
      const view = await loadLoadout(id);
      saved = view;
      resetPendingFromSaved(view);
    } catch (error) {
      loadError = messageOf(error);
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    const id = shipId;
    void loggedIn;
    void load(id);
  });

  const remaining = $derived(remainingOf(pending));

  const cost = $derived(saved === null ? 0 : costOf(saved.stats, pending, saved.creditsPerPoint));

  function canIncrease(key: StatKey): boolean {
    return canIncreaseStat(pending, key, loggedIn);
  }

  function canDecrease(key: StatKey): boolean {
    return canDecreaseStat(pending, key, loggedIn);
  }

  function increase(key: StatKey): void {
    if (!canIncrease(key)) return;
    pending = { ...pending, [key]: pending[key] + 1 };
  }

  function decrease(key: StatKey): void {
    if (!canDecrease(key)) return;
    pending = { ...pending, [key]: pending[key] - 1 };
  }

  function defaultAbility(slot: 1 | 2): number {
    return upgradeForPath(shipId, slot);
  }

  function abilityFor(slot: 1 | 2): number {
    const override = slot === 1 ? pendingSlot1 : pendingSlot2;
    return override ?? defaultAbility(slot);
  }

  function poolFor(slot: 1 | 2): readonly number[] {
    return slot === 1 ? (saved?.slot1Pool ?? []) : (saved?.slot2Pool ?? []);
  }

  function openPicker(slot: 1 | 2): void {
    if (!loggedIn || confirming || poolFor(slot).length === 0) return;
    pickerSlot = slot;
  }

  function closePicker(): void {
    pickerSlot = null;
  }

  function pick(slot: 1 | 2, abilityId: number): void {
    const override = abilityId === defaultAbility(slot) ? null : abilityId;
    if (slot === 1) pendingSlot1 = override;
    else pendingSlot2 = override;
    pickerSlot = null;
  }

  function pickerHeight(slot: 1 | 2): number {
    const rows = LOADOUT_SCREEN.picker.list.top + LOADOUT_SCREEN.picker.list.bottom;
    return Math.min(
      rows + poolFor(slot).length * LOADOUT_SCREEN.picker.row.height,
      LOADOUT_SCREEN.picker.panel.maxHeight,
    );
  }

  const activeAbilityId = $derived(upgradeForPath(shipId, 0));

  const canConfirm = $derived(
    canConfirmBuild({
      loggedIn,
      stats: pending,
      cost,
      credits,
      confirming,
      hasSaved: saved !== null,
    }),
  );

  async function confirm(): Promise<void> {
    if (!canConfirm) return;
    confirming = true;
    confirmError = '';
    try {
      const result = await confirmBuild(
        shipId,
        { stats: pending, slot1AbilityId: pendingSlot1, slot2AbilityId: pendingSlot2 },
        cost,
      );
      saved = result.loadout;
      resetPendingFromSaved(result.loadout);
    } catch (error) {
      confirmError = messageOf(error);
    } finally {
      confirming = false;
    }
  }

  const pillState = pillStateOf;

  const PILL_GROUPS = [0, 1, 2, 3, 4, 5];

  const {
    inset,
    panel,
    dim,
    close,
    heading,
    credits: creditsLayout,
    portrait: portraitLayout,
    shipSelect: shipSelectLayout,
    stats: statsLayout,
    row: rowLayout,
    label: labelLayout,
    pill: pillLayout,
    readout: readoutLayout,
    stepper,
    slots: slotsLayout,
    slotLabel,
    ability: abilityLayout,
    locked: lockedLayout,
    footer: footerLayout,
    remaining: remainingLayout,
    confirm: confirmLayout,
    picker,
  } = LOADOUT_SCREEN;

  const closeBottom = -(close.height + close.centreYFromBottom);
</script>

<div
  class="loadout"
  role="dialog"
  aria-modal="true"
  aria-label="Loadout for {shipName(shipId)}"
  data-testid="loadout-screen"
>
  <div class="loadout__dim" style:background-color={dim.color} style:opacity={dim.alpha}></div>

  <div
    class="loadout__panel"
    style:width="min({panel.maxWidth}px, 100% - {inset * 2}px)"
    style:height="min({panel.maxHeight}px, 100% - {inset * 2}px)"
  >
    <MetalFrame />

    <h2
      class="loadout__heading"
      style:top="{heading.top}px"
      style:font-size="{heading.font}px"
      style:color={heading.color}
    >
      Loadout
    </h2>

    <span
      class="loadout__credits"
      data-testid="loadout-credits"
      style:top="{creditsLayout.top}px"
      style:right="{creditsLayout.right}px"
      style:font-size="{creditsLayout.font}px"
    >
      <span class="loadout__credits-label" style:font-size="{creditsLayout.labelFont}px"
        >Credits</span
      >
      {credits}
    </span>

    <span
      class="loadout__portrait"
      style:top="{portraitLayout.top}px"
      style:left="{portraitLayout.left}px"
      style:width="{portraitLayout.size}px"
      style:height="{portraitLayout.size}px"
    >
      <ShipArt {team} {shipId} size={portraitLayout.size} />
    </span>
    <select
      class="wis-select wis-select--wide loadout__ship"
      data-testid="loadout-ship"
      aria-label="Ship"
      bind:value={shipId}
      style:top="{shipSelectLayout.top}px"
      style:left="{shipSelectLayout.left}px"
      style:width="{shipSelectLayout.width}px"
      style:font-size="{shipSelectLayout.font}px"
    >
      {#each SHIP_NAMES as name, id (id)}
        <option value={id}>{name}</option>
      {/each}
    </select>

    {#if loading}
      <p class="loadout__note">Loading…</p>
    {:else if loadError !== ''}
      <p class="loadout__note loadout__note--bad">{loadError}</p>
    {:else}
      <div
        class="loadout__stats"
        style:top="{statsLayout.top}px"
        style:left="{statsLayout.left}px"
        style:width="{statsLayout.width}px"
      >
        {#each STAT_KEYS as key (key)}
          {@const savedValue = saved?.stats[key] ?? STAT_DEFAULT}
          <div
            class="loadout__row"
            style:height="{rowLayout.height}px"
            style:margin-bottom="{rowLayout.marginBottom}px"
          >
            <span
              class="loadout__label"
              style:width="{labelLayout.width}px"
              style:font-size="{labelLayout.font}px"
              style:color={STAT_COLORS[key]}>{STAT_LABELS[key]}</span
            >
            <span
              class="loadout__pills"
              data-testid="loadout-pills-{key}"
              style:gap="{pillLayout.gap}px"
            >
              {#each PILL_GROUPS as group (group)}
                <span class="loadout__pill-group" style:gap="{pillLayout.groupGap}px">
                  {#each [0, 1, 2, 3, 4] as offset (offset)}
                    {@const index = group * 5 + offset}
                    <i
                      class="loadout__pill loadout__pill--{pillState(
                        savedValue,
                        pending[key],
                        index,
                      )}"
                      style:width="{pillLayout.width}px"
                      style:height="{pillLayout.height}px"
                      style:border-radius="{pillLayout.borderRadius}px"
                      style:--loadout-color={STAT_COLORS[key]}
                    ></i>
                  {/each}
                </span>
              {/each}
            </span>
            <span
              class="loadout__readout"
              style:width="{readoutLayout.width}px"
              style:margin-left="{readoutLayout.marginLeft}px"
              style:font-size="{readoutLayout.font}px">{pending[key]}/{STAT_MAX}</span
            >
            <button
              class="wis-btn-square loadout__minus"
              type="button"
              aria-label="Decrease {STAT_LABELS[key]}"
              disabled={!canDecrease(key)}
              style:width="{stepper.width}px"
              style:height="{stepper.height}px"
              style:margin-left="{stepper.marginLeft}px"
              style:font-size="{stepper.font}px"
              onclick={() => {
                decrease(key);
              }}>−</button
            >
            <button
              class="wis-btn-square loadout__plus"
              type="button"
              aria-label="Increase {STAT_LABELS[key]}"
              disabled={!canIncrease(key)}
              style:width="{stepper.width}px"
              style:height="{stepper.height}px"
              style:margin-left="{stepper.marginLeft}px"
              style:font-size="{stepper.font}px"
              onclick={() => {
                increase(key);
              }}>+</button
            >
          </div>
        {/each}
      </div>

      <div
        class="loadout__slots"
        style:top="{slotsLayout.top}px"
        style:left="{slotsLayout.left}px"
        style:width="{slotsLayout.width}px"
        style:height="{slotsLayout.height}px"
        style:gap="{slotsLayout.gap}px"
      >
        <div
          class="loadout__slot"
          style:width="{slotsLayout.slotWidth}px"
          style:height="{slotsLayout.slotHeight}px"
        >
          <span class="loadout__slot-label" style:font-size="{slotLabel.font}px">Active</span>
          <span
            class="loadout__ability wis-tint"
            style:font-size="{abilityLayout.font}px"
            style:color={abilityLayout.color}>{upgradeLabel(activeAbilityId).name}</span
          >
          <span
            class="loadout__locked"
            style:font-size="{lockedLayout.font}px"
            style:color={lockedLayout.color}>Locked</span
          >
        </div>

        {#each [1, 2] as slot (slot)}
          {@const ability = abilityFor(slot as 1 | 2)}
          {@const swappable = poolFor(slot as 1 | 2).length > 0}
          <div
            class="loadout__slot"
            style:width="{slotsLayout.slotWidth}px"
            style:height="{slotsLayout.slotHeight}px"
          >
            <span class="loadout__slot-label" style:font-size="{slotLabel.font}px">Slot {slot}</span
            >
            <span
              class="loadout__ability wis-tint"
              style:font-size="{abilityLayout.font}px"
              style:color={abilityLayout.color}
            >
              {upgradeLabel(ability).name}
            </span>
            {#if swappable}
              <button
                class="wis-btn loadout__swap"
                type="button"
                data-testid="loadout-slot-{slot}"
                disabled={!loggedIn || confirming}
                onclick={() => {
                  openPicker(slot as 1 | 2);
                }}>Swap</button
              >
            {:else}
              <span
                class="loadout__locked"
                style:font-size="{lockedLayout.font}px"
                style:color={lockedLayout.color}>Locked</span
              >
            {/if}
          </div>
        {/each}
      </div>

      <div class="loadout__footer" style:bottom="{footerLayout.bottom}px">
        {#if !loggedIn}
          <span class="loadout__note loadout__note--inline">Log in to customise a ship</span>
        {:else}
          {#if remaining !== 0}
            <span
              class="loadout__remaining"
              data-testid="loadout-remaining"
              style:font-size="{remainingLayout.font}px"
            >
              {remaining} point{remaining === 1 ? '' : 's'} remaining
            </span>
          {/if}
          {#if cost > credits}
            <span class="loadout__note loadout__note--inline loadout__note--bad"
              >Not enough credits</span
            >
          {/if}
          {#if confirmError !== ''}
            <span
              class="loadout__note loadout__note--inline loadout__note--bad"
              data-testid="loadout-confirm-error">{confirmError}</span
            >
          {/if}
          <button
            class="wis-btn-buy loadout__confirm"
            type="button"
            data-testid="loadout-confirm"
            disabled={!canConfirm}
            style:font-size="{confirmLayout.font}px"
            style:padding="{confirmLayout.paddingV}px {confirmLayout.paddingH}px"
            onclick={() => {
              void confirm();
            }}>{confirming ? 'Confirming…' : `Confirm — ${String(cost)} credits`}</button
          >
        {/if}
      </div>
    {/if}

    <button
      class="wis-btn loadout__close"
      type="button"
      data-testid="loadout-close"
      onclick={onclose}
      style:bottom="{closeBottom}px"
      style:left="{((1 - close.widthFraction) / 2) * 100}%"
      style:width="{close.widthFraction * 100}%"
      style:height="{close.height}px"
      style:font-size="{close.font}px">{close.label}</button
    >
  </div>

  {#if pickerSlot !== null}
    {@const slot = pickerSlot}
    <div
      class="loadout__picker"
      role="dialog"
      aria-modal="true"
      aria-label="Choose an ability"
      data-testid="loadout-picker"
    >
      <div
        class="loadout__picker-dim"
        style:background-color={dim.color}
        style:opacity={dim.alpha}
      ></div>

      <div
        class="loadout__picker-panel"
        style:width="min({picker.panel.maxWidth}px, 100% - {inset * 2}px)"
        style:height="min({pickerHeight(slot)}px, 100% - {inset * 2}px)"
      >
        <MetalFrame />

        <h3
          class="loadout__picker-heading"
          style:top="{picker.heading.top}px"
          style:font-size="{picker.heading.font}px"
          style:color={picker.heading.color}
        >
          Slot {slot}
        </h3>

        <div
          class="loadout__picker-list"
          style:top="{picker.list.top}px"
          style:left="{picker.list.side}px"
          style:right="{picker.list.side}px"
          style:bottom="{picker.list.bottom}px"
        >
          {#each poolFor(slot) as abilityId (abilityId)}
            <button
              class="loadout__picker-row"
              class:loadout__picker-row--current={abilityFor(slot) === abilityId}
              type="button"
              data-testid="loadout-picker-option"
              style:height="{picker.row.height}px"
              style:gap="{picker.row.gap}px"
              style:font-size="{picker.row.font}px"
              onclick={() => {
                pick(slot, abilityId);
              }}
            >
              <span
                class="loadout__abbr"
                style:width="{picker.row.abbrWidth}px"
                style:font-size="{picker.row.abbrFont}px">{upgradeLabel(abilityId).abbr}</span
              >
              <span class="loadout__picker-name">{upgradeLabel(abilityId).name}</span>
              {#if abilityId === defaultAbility(slot)}
                <span class="loadout__picker-default" style:font-size="{picker.row.abbrFont}px"
                  >default</span
                >
              {/if}
            </button>
          {/each}
        </div>

        <button
          class="wis-btn loadout__picker-cancel"
          type="button"
          data-testid="loadout-picker-cancel"
          style:bottom="{picker.cancel.bottom}px"
          style:font-size="{picker.cancel.font}px"
          onclick={closePicker}>Cancel</button
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .loadout {
    position: fixed;
    inset: 0;
    z-index: 10;
    display: grid;
    place-items: center;
    pointer-events: auto;
  }

  .loadout__dim {
    position: absolute;
    inset: 0;
  }

  .loadout__panel {
    position: relative;
  }

  .loadout__close {
    position: absolute;
    padding: 0;
    color: #f2f2f2;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .loadout__heading {
    position: absolute;
    pointer-events: none;
    left: 0;
    width: 100%;
    margin: 0;
    font-weight: normal;
    text-align: center;
  }

  .loadout__credits {
    position: absolute;
    pointer-events: none;
    color: #f4f4f4;
    text-align: center;
    text-shadow: 0 1px 2px #000;
  }

  .loadout__credits-label {
    display: block;
  }

  .loadout__portrait {
    position: absolute;
    pointer-events: none;
    display: grid;
    place-items: center;
  }

  .loadout__ship {
    position: absolute;
    text-align: center;
  }

  .loadout__note {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    text-align: center;
    color: #222;
    pointer-events: none;
  }

  .loadout__note--inline {
    position: static;
    width: auto;
  }

  .loadout__note--bad {
    color: #ff7263;
  }

  .loadout__stats {
    position: absolute;
  }

  .loadout__row {
    position: relative;
    display: flex;
    align-items: center;
  }

  .loadout__label {
    flex: none;
    text-shadow:
      0 1px 2px rgb(0 0 0 / 70%),
      0 0 6px rgb(0 0 0 / 45%);
  }

  .loadout__swap {
    color: #f2f2f2;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .loadout__pills {
    display: flex;
    flex: none;
  }

  .loadout__pill-group {
    display: flex;
  }

  .loadout__pill {
    background: #2a2a2a;
  }

  .loadout__pill--settled {
    background: var(--loadout-color);
    box-shadow:
      0 0 4px var(--loadout-color),
      0 1px 2px #000;
  }

  .loadout__pill--pending-filled {
    background: var(--loadout-color);
    box-shadow: 0 0 0 1px #fff inset;
  }

  .loadout__pill--pending-emptied {
    background: color-mix(in srgb, var(--loadout-color) 30%, #101010);
    box-shadow: 0 0 0 2px var(--loadout-color) inset;
  }

  .loadout__readout {
    flex: none;
    text-align: right;
    color: #222;
  }

  .loadout__minus,
  .loadout__plus {
    flex: none;
    padding: 0;
  }

  .loadout__slots {
    position: absolute;
    display: flex;
  }

  .loadout__slot {
    position: relative;
    box-shadow: inset 0 2px 6px rgb(0 0 0 / 40%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .loadout__slot-label {
    color: #222;
  }

  .loadout__ability {
    width: 100%;
    text-align: center;
  }

  .loadout__footer {
    position: absolute;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .loadout__remaining {
    color: #222;
  }

  .loadout__picker {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
  }

  .loadout__picker-dim {
    position: absolute;
    inset: 0;
  }

  .loadout__picker-panel {
    position: relative;
  }

  .loadout__picker-heading {
    position: absolute;
    pointer-events: none;
    left: 0;
    width: 100%;
    margin: 0;
    font-weight: normal;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  .loadout__picker-list {
    position: absolute;
    overflow-y: auto;
    box-shadow: inset 0 2px 6px rgb(0 0 0 / 40%);
  }

  .loadout__picker-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 12px;
    color: var(--wis-ink);
    font-family: var(--wis-font);
    text-transform: uppercase;
    letter-spacing: 1px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }

  .loadout__picker-row:hover {
    background: rgb(255 255 255 / 35%);
  }

  .loadout__picker-name {
    flex: 1 1 auto;
  }

  .loadout__picker-default {
    flex: none;
    color: var(--wis-ink-soft);
  }

  .loadout__picker-row--current {
    background: rgb(155 240 79 / 30%);
    box-shadow: inset 0 0 0 1px rgb(94 150 43 / 70%);
  }

  .loadout__abbr {
    flex: none;
    color: #4c7a1e;
  }

  .loadout__picker-cancel {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #f2f2f2;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .loadout__confirm {
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }
</style>
